/**
 * Vehicles seen at an address during an attempt (optional, any outcome).
 * The pick lists cover common U.S. makes/models; "Other" allows free text.
 */

export const VEHICLE_MODELS: Record<string, string[]> = {
  Acura: ['ILX', 'Integra', 'MDX', 'RDX', 'TLX', 'TL', 'TSX'],
  Audi: ['A3', 'A4', 'A6', 'Q3', 'Q5', 'Q7'],
  BMW: ['3 Series', '5 Series', 'X1', 'X3', 'X5'],
  Buick: ['Enclave', 'Encore', 'Envision', 'LaCrosse', 'LeSabre', 'Regal'],
  Cadillac: ['ATS', 'CTS', 'Escalade', 'SRX', 'XT4', 'XT5'],
  Chevrolet: ['Blazer', 'Camaro', 'Colorado', 'Cruze', 'Equinox', 'Express', 'Impala', 'Malibu', 'Silverado 1500', 'Silverado 2500/3500', 'Sonic', 'Spark', 'Suburban', 'Tahoe', 'Trailblazer', 'Traverse', 'Trax'],
  Chrysler: ['200', '300', 'Pacifica', 'Town & Country', 'PT Cruiser'],
  Dodge: ['Challenger', 'Charger', 'Durango', 'Grand Caravan', 'Journey', 'Dakota', 'Ram (older)'],
  Ford: ['Bronco', 'Crown Victoria', 'E-Series Van', 'Edge', 'Escape', 'Expedition', 'Explorer', 'F-150', 'F-250/350', 'Focus', 'Fusion', 'Mustang', 'Ranger', 'Taurus', 'Transit'],
  GMC: ['Acadia', 'Canyon', 'Envoy', 'Sierra 1500', 'Sierra 2500/3500', 'Terrain', 'Yukon'],
  Honda: ['Accord', 'Civic', 'CR-V', 'Fit', 'HR-V', 'Odyssey', 'Pilot', 'Ridgeline'],
  Hyundai: ['Accent', 'Elantra', 'Kona', 'Palisade', 'Santa Fe', 'Sonata', 'Tucson'],
  Infiniti: ['G35/G37', 'Q50', 'QX60', 'QX80'],
  Jeep: ['Cherokee', 'Compass', 'Gladiator', 'Grand Cherokee', 'Liberty', 'Patriot', 'Renegade', 'Wrangler'],
  Kia: ['Forte', 'K5', 'Optima', 'Rio', 'Sorento', 'Soul', 'Sportage', 'Telluride'],
  Lexus: ['ES', 'GX', 'IS', 'NX', 'RX'],
  Lincoln: ['Aviator', 'MKZ', 'Navigator', 'Town Car'],
  Mazda: ['CX-30', 'CX-5', 'CX-9', 'Mazda3', 'Mazda6'],
  'Mercedes-Benz': ['C-Class', 'E-Class', 'GLC', 'GLE', 'Sprinter'],
  Mercury: ['Grand Marquis', 'Mariner', 'Milan', 'Mountaineer'],
  Mitsubishi: ['Eclipse', 'Galant', 'Lancer', 'Mirage', 'Outlander'],
  Nissan: ['Altima', 'Armada', 'Frontier', 'Maxima', 'Murano', 'Pathfinder', 'Rogue', 'Sentra', 'Titan', 'Versa'],
  Pontiac: ['G6', 'Grand Am', 'Grand Prix', 'Vibe'],
  Ram: ['1500', '2500/3500', 'ProMaster'],
  Subaru: ['Crosstrek', 'Forester', 'Impreza', 'Legacy', 'Outback'],
  Tesla: ['Model 3', 'Model S', 'Model X', 'Model Y'],
  Toyota: ['4Runner', 'Avalon', 'Camry', 'Corolla', 'Highlander', 'Prius', 'RAV4', 'Sequoia', 'Sienna', 'Tacoma', 'Tundra'],
  Volkswagen: ['Atlas', 'Beetle', 'Golf', 'Jetta', 'Passat', 'Tiguan'],
  Volvo: ['S60', 'XC60', 'XC90'],
  Motorcycle: ['Harley-Davidson', 'Honda', 'Kawasaki', 'Suzuki', 'Yamaha', 'Other'],
  Trailer: ['Utility', 'Enclosed', 'Camper / RV', 'Boat trailer'],
};

export const VEHICLE_MAKES = Object.keys(VEHICLE_MODELS);

export const VEHICLE_COLORS = ['Black', 'White', 'Silver', 'Gray', 'Red', 'Blue', 'Dark blue', 'Green', 'Brown', 'Tan / Beige', 'Gold', 'Yellow', 'Orange', 'Purple', 'Maroon', 'Two-tone'];

export const US_STATES = ['SC', 'NC', 'GA', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'AK'];

export interface SeenVehicle {
  make: string | null;
  model: string | null;
  color: string | null;
  plate: string | null;
  plateState: string | null;
  photoDocumentId?: string | null;
}

/** "Black Chevrolet Silverado 1500 · SC ABC123" */
export function formatVehicle(v: Partial<SeenVehicle>): string {
  const desc = [v.color, v.make, v.model].filter(Boolean).join(' ');
  const tag = v.plate ? `${v.plateState ? `${v.plateState} ` : ''}${v.plate}` : '';
  return [desc, tag].filter(Boolean).join(' · ') || 'Vehicle';
}

/** Same vehicle seen on different visits: by tag when known, else by description. */
export function vehicleKey(v: Partial<SeenVehicle>): string {
  return v.plate ? `tag:${(v.plateState ?? '').toUpperCase()}${v.plate.toUpperCase().replace(/[^A-Z0-9]/g, '')}` : `desc:${[v.color, v.make, v.model].join('|').toLowerCase()}`;
}
